import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { findDOMNode } from 'react-dom'
import { DragSource, DropTarget } from 'react-dnd'
import classnames from 'classnames'
import Moment from 'moment'
import momentDurationFormatSetup from 'moment-duration-format'
import ImmutableProptypes from 'react-immutable-proptypes'
import { connect as connectRedux } from 'react-redux'
import Dropdown from 'rc-dropdown'
import Menu, { Item as MenuItem, Divider } from 'rc-menu'
import 'rc-dropdown/assets/index.css'

import Icon from '_components/icon'

import styles from './styles.css'

momentDurationFormatSetup(Moment)

const cardSource = {
  beginDrag(props) {
    return {
      id: props.id,
      index: props.index,
    }
  },
  // eslint-disable-next-line no-unused-vars
  endDrag(props, monitor) {
    props.saveOrdering()
  },
}

const cardTarget = {
  hover(props, monitor, component) {
    const dragIndex = monitor.getItem().index
    const hoverIndex = props.index

    // Don't replace items with themselves
    if (dragIndex === hoverIndex) {
      return
    }

    // Determine rectangle on screen
    const hoverBoundingRect = findDOMNode(component).getBoundingClientRect() // eslint-disable-line react/no-find-dom-node

    // Get vertical middle
    const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

    // Determine mouse position
    const clientOffset = monitor.getClientOffset()

    // Get pixels to the top
    const hoverClientY = clientOffset.y - hoverBoundingRect.top

    // Only perform the move when the mouse has crossed half of the items height
    // When dragging downwards, only move when the cursor is below 50%
    // When dragging upwards, only move when the cursor is above 50%

    // Dragging downwards
    if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
      return
    }

    // Dragging upwards
    if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
      return
    }

    // Time to actually perform the action
    props.moveCard(dragIndex, hoverIndex)

    // Note: we're mutating the monitor item here!
    // Generally it's better to avoid mutations,
    // but it's good here for the sake of performance
    // to avoid expensive index searches.
    monitor.getItem().index = hoverIndex // eslint-disable-line no-param-reassign
  },
}

const mapStateToProps = ({ counter, currentTask, settings }) => ({
  counter,
  currentTask,
  settings,
})

@DropTarget('card', cardTarget, connect => ({
  connectDropTarget: connect.dropTarget(),
}))
@DragSource('card', cardSource, (connect, monitor) => ({
  connectDragSource: connect.dragSource(),
  isDragging: monitor.isDragging(),
}))
class OrderingCard extends PureComponent {
  static propTypes = {
    connectDragSource: PropTypes.func.isRequired,
    connectDropTarget: PropTypes.func.isRequired,
    isDragging: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    deleteCard: PropTypes.func.isRequired,
    editCard: PropTypes.func.isRequired,
    checkCard: PropTypes.func.isRequired,
    shiftCard: PropTypes.func.isRequired,
    moveToSectionCard: PropTypes.func.isRequired,
    section: PropTypes.number.isRequired,
    createCalendarCard: PropTypes.func.isRequired,
  }

  static defaultProps = {
    name: '',
    status: 1,
    currentTask: new Map(),
    counter: new Map(),
    settings: new Map(),
  }

  onEditClick = () => {
    const { editCard, id } = this.props
    editCard(id)
  }

  onStatusClick = () => {
    const { checkCard, id } = this.props
    checkCard(id)
  }

  onShiftClick = () => {
    const { shiftCard, id } = this.props
    shiftCard(id)
  }

  onCheckClick = () => {
    const { checkCard, id } = this.props
    checkCard(id)
  }

  onDeleteClick = () => {
    const { deleteCard, id } = this.props
    deleteCard(id)
  }

  onMoveToSection = sectionId => {
    const { moveToSectionCard, id } = this.props
    moveToSectionCard(id, sectionId)
  }

  onCreateCalendarClick = () => {
    const { createCalendarCard, name } = this.props
    createCalendarCard({ name })
  }

  renderShift = () => {
    const { status, date } = this.props
    if (status !== 2 && new Date(date).toDateString() !== new Date().toDateString()) {
      return (
        <button className={classnames(styles.button)} onClick={this.onShiftClick}>
          <i className={classnames('fa fa-arrow-up')} />
        </button>
      )
    }
    return null
  }

  handleClick = ({ key }) => {
    switch (key) {
      case '1':
        this.onDeleteClick()
        break
      case '2':
        this.onMoveToSection(0)
        break
      case '3':
        this.onMoveToSection(100)
        break
      case '4':
        this.onMoveToSection(200)
        break
      case '5':
        this.onMoveToSection(300)
        break
      case '6':
        this.onMoveToSection(400)
        break
      case '7':
        this.onCreateCalendarClick()
        break
      default:
    }
    return null
  }

  menu = () => {
    const { section } = this.props
    return (
      <Menu onClick={this.handleClick} selectable={false}>
        <MenuItem key="1">Delete</MenuItem>
        {section !== 0 && (
          <MenuItem key="2">
            Move to {this.props.settings.get('tomorrowSection0') || 'Recurring'}
          </MenuItem>
        )}
        {section !== 100 && (
          <MenuItem key="3">
            Move to {this.props.settings.get('tomorrowSection1') || 'Tomorrow'}
          </MenuItem>
        )}
        {section !== 200 && (
          <MenuItem key="4">
            Move to {this.props.settings.get('tomorrowSection2') || 'Later'}
          </MenuItem>
        )}
        {section !== 300 && (
          <MenuItem key="5">
            Move to {this.props.settings.get('tomorrowSection3') || 'Much Later'}
          </MenuItem>
        )}
        {section !== 400 && (
          <MenuItem key="6">
            Move to {this.props.settings.get('tomorrowSection4') || 'Never'}
          </MenuItem>
        )}
        <MenuItem key="7">Add to Calendar</MenuItem>
      </Menu>
    )
  }

  render() {
    const { isDragging, connectDragSource, connectDropTarget, name, status } = this.props
    const opacity = isDragging ? 0 : 1

    return connectDragSource(
      connectDropTarget(
        <div className={classnames(styles.cardContainer)} style={{ opacity }}>
          <div className={classnames(styles.ellipsis)}>
            <i className="fa fa-ellipsis-v" />
            <i className="fa fa-ellipsis-v" style={{ marginLeft: 2 }} />
          </div>
          <div className={classnames(styles.ellipsis)}>
            <button className={classnames(styles.button)} onClick={this.onStatusClick}>
              <i className={classnames('hmm-icon-square-empty')} />
            </button>
          </div>
          <div className={classnames(styles.name)} onClick={status != 2 ? this.onEditClick : null}>
            {name}
          </div>
          {this.renderShift()}
          <Dropdown trigger={['click']} overlay={this.menu()} animation="slide-up">
            <button className={classnames(styles.button)}>
              <i className={classnames('fa fa-ellipsis-v')} />
            </button>
          </Dropdown>
        </div>
      )
    )

    // return connectDragSource(
    //   connectDropTarget(
    //     <div className={classnames(styles.Grid, styles['Grid--alignMiddle'])} style={{ opacity }}>
    //       <div className={classnames(styles['Grid-cell'], styles['u-sizeFit'], styles.ellipsis)}>
    //         <i className="fa fa-ellipsis-v" />
    //         <i className="fa fa-ellipsis-v" style={{ marginLeft: 2 }} />
    //       </div>
    //       <div className={classnames(styles['Grid-cell'], styles['u-sizeFit'], styles.ellipsis)}>
    //         <button
    //           className={classnames(styles['Grid-cell'], styles['u-sizeFit'], styles.button)}
    //           onClick={this.onStatusClick}
    //         >
    //           <i className={classnames('hmm-icon-square-empty')} />
    //         </button>
    //       </div>
    //       <div
    //         className={classnames(styles['Grid-cell'], styles['u-sizeFill'], styles.name)}
    //         onClick={status != 2 ? this.onEditClick : null}
    //       >
    //         {name}
    //       </div>
    //       {this.renderShift()}
    //       <Dropdown trigger={['click']} overlay={this.menu()} animation="slide-up">
    //         <button
    //           className={classnames(styles['Grid-cell'], styles['u-sizeFit'], styles.deleteButton)}
    //         >
    //           <i className={classnames('fa fa-ellipsis-v')} />
    //         </button>
    //       </Dropdown>
    //     </div>
    //   )
    // )
  }
}

export default connectRedux(mapStateToProps, null)(OrderingCard)



// WEBPACK FOOTER //
// ./src/views/tomorrow-items/ordering-card/index.js